import React from 'react';
import {FormattedMessage} from 'react-intl';
import {FlexColumn, Modal, Text} from '@forecast-it/design-system';
import {useReportBuilder} from './ReportBuilderContext';
import {useRouterPrompt} from '../../forecast-app/shared/hooks/useRouterPrompt';

/** Guards in-app navigation away from the builder with a Leave/Cancel overlay. Renders `null`
 * unless the blocker has actually caught a navigation attempt, so mounting it does not affect
 * layout. There is no save path here — the builder has no save-without-publishing, and publishing
 * from a navigation guard is too heavy an action to bury in it, so leaving is the only way
 * forward and cancelling is the only way back. */
export function LeaveBuilderPrompt(): JSX.Element | null {
	const {definition, dirty, openedFromStorage, pendingName, savedReportId} = useReportBuilder();

	// A name typed into the rename field but not yet submitted is real work the editor would lose, and
	// it is deliberately invisible to `dirty`: staging exists precisely so an uncommitted name does not
	// mark the report changed. That makes it invisible to this guard too unless it is asked about
	// directly. Whitespace-only and unchanged drafts are excluded for the same reason `commitPendingName`
	// discards them — there is nothing to lose.
	const stagedName = pendingName === null ? null : pendingName.trim();
	const hasStagedRename = stagedName !== null && stagedName !== '' && stagedName !== definition.name;
	// Broader than the unsaved-changes pill on purpose: a report with no copy in storage exists only
	// in router location state (or only in this session's edits), so leaving destroys it outright
	// even when nothing has been edited since. `openedFromStorage` and `savedReportId` each answer
	// that "does a copy exist in storage" question for a different half of the report's lifetime, and
	// BOTH are needed — neither alone is reliable:
	//   - `openedFromStorage` is set once, from the route, and never changes again during the
	//     session. It is true for a report that already existed in storage when the builder opened
	//     (including one whose sharing flag came back unrecognised, which is why `savedReportId`
	//     alone used to false-positive here), but it stays false for a chat draft even after that
	//     draft is saved/published during this session — it does not track later saves.
	//   - `savedReportId` DOES track later saves (`save()`/`publish()` set it once the first save
	//     lands), which is exactly what `openedFromStorage` misses for a freshly published chat
	//     draft. But it is unreliable at the other end: a report opened from storage whose sharing
	//     flag was unrecognised is deliberately left with no `savedReportId` seed (see
	//     `ReportBuilderPage`), even though it is genuinely stored.
	// Dropping either signal reintroduces the gap the other one covers, so do not simplify this to
	// just one of them.
	const hasChanges = dirty || hasStagedRename || (!openedFromStorage && savedReportId === null);
	const {showPrompt, handleDiscard, closePrompt} = useRouterPrompt({hasChanges});

	return showPrompt ? (
		<Modal onCloseModal={closePrompt}>
			<Modal.Title>
				<FormattedMessage id={'ai_report_builder.leave_prompt_title'} />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn>
					<Text>
						<FormattedMessage id={'ai_report_builder.leave_prompt_body'} />
					</Text>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={handleDiscard}>
					<FormattedMessage id={'ai_report_builder.leave_prompt_confirm'} />
				</Modal.PrimaryFooterButton>
				<Modal.TertiaryFooterButton onPress={closePrompt}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.TertiaryFooterButton>
			</Modal.Footer>
		</Modal>
	) : null;
}
